
import React, { useRef, useState } from "react";
import { HiOutlineVideoCamera, HiOutlineSparkles } from "react-icons/hi";
import { PiSmileyFill } from "react-icons/pi";

import PostButton from "../PostButton";
import ToastMessage from "../ToastMessage";
import FileGridUploader from "../FileGridUpload";
import EmojiReactPicker from "../EmojiReactPicker";
import { useUploads } from "../../context/UploadContext";
import { validatePostVideos, VIDEO_TYPES, MAX_POST_VIDEOS, MAX_POST_VIDEO_SIZE } from "../../lib/utils";
import { compressVideos } from "../../lib/compressVideo";

const Video = ({ close }) => {
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const smileyRef = useRef(null);
  const { startUpload } = useUploads();

  const type = "video";
  const maxCaptionLength = 500;
  const maxFiles = MAX_POST_VIDEOS;
  const maxFileSize = MAX_POST_VIDEO_SIZE;
  const allowedTypes = VIDEO_TYPES;


const handleSubmit = async (e) => {
  e?.preventDefault?.();
  if (isSubmitting) return;

  setIsSubmitting(true);

  try {
    const selectedVideos = files.map((item) => item instanceof File ? item : item.file);

    // Validate selected files
    if (!selectedVideos.length || selectedVideos.some((file) => !(file instanceof File))) {
      ToastMessage("Please select valid video files.");
      return;
    }

    // Maximum number of videos
    if (selectedVideos.length > maxFiles) {
      ToastMessage(`You can upload a maximum of ${maxFiles} videos.`);
      return;
    }

    // Allowed video formats
    const invalidType = selectedVideos.find((file) => !allowedTypes.includes(file.type));

    if (invalidType) {
      ToastMessage(`${invalidType.name} is not a supported video format. Use MP4, MOV, or WebM.`);
      return;
    }

    // Maximum file size
    const oversizedFile = selectedVideos.find((file) => file.size > maxFileSize);

    if (oversizedFile) {
      ToastMessage(`${oversizedFile.name} exceeds the 100 MB limit.`);
      return;
    }

    // Validate video metadata and duration
    const validation = await validatePostVideos(selectedVideos);

    if (!validation.valid) {
      ToastMessage(validation.error);
      return;
    }

    console.log("Alakoze video validation:", validation.metadata);

    // Compress videos while preserving audio
    const compressed = await compressVideos(selectedVideos, {
  minSize: 20 * 1024 * 1024,
  maxSize: 50 * 1024 * 1024,
  maxDuration: 120,
  minBitrate: 2500000,
  crf: 28,
  preset: "ultrafast",
  maxWidth: 1280,
  encodingTimeout: 30000,
});

    console.log("Alakoze video compression:", {
      originalMB: compressed.originalMB,
      compressedMB: compressed.compressedMB,
      savedPercent: `${compressed.savedPercent}%`,
    });

    // Prepare post
    const formData = new FormData();
    formData.append("caption", caption.trim());
    formData.append("type", type);
    compressed.files.forEach((file) => formData.append("content", file));

    // Start background upload
    await startUpload({
      formData,
      name: compressed.files.length === 1 ? compressed.files[0].name : `${compressed.files.length} videos`,
    });

    // Reset form
    setFiles([]);
    setCaption("");
    setShowEmoji(false);
    close?.();

    ToastMessage("Your videos are uploading in the background.");
  } catch (error) {
    console.error("Video preparation failed:", error);
    ToastMessage(error?.message || "Unable to prepare your videos.");
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <div className="w-full overflow-x-hidden">
      <form onSubmit={handleSubmit}>
        {/* ================= VIDEO HEADER ================= */}

        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <HiOutlineVideoCamera size={21} />
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900">Video post</h3>
              <p className="mt-0.5 text-xs text-gray-400">Share up to 10 videos</p>
            </div>
          </div>

          {files.length > 0 && <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-600">{files.length}/{maxFiles}</span>}
        </div>

        {/* ================= VIDEO UPLOAD ================= */}

        <div className="relative overflow-visible rounded-2xl border border-gray-200 bg-gray-50/40 p-2 transition-all duration-200 hover:border-gray-300">
          <FileGridUploader allowMultiple maxFiles={maxFiles} files={files} setFiles={setFiles} acceptedFileTypes=".mp4,.webm,.mov" />
        </div>

        {/* ================= UPLOAD INFO ================= */}

        {files.length === 0 && (
          <div className="mt-2 flex items-center gap-1.5 px-1 text-[11px] text-gray-400">
            <HiOutlineSparkles size={14} className="text-blue-400" />
            MP4, MOV and WebM videos (up to 100 MB each)
          </div>
        )}

        {/* ================= CAPTION ================= */}

        <div className="mt-5 overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all duration-200 focus-within:border-blue-300 focus-within:ring-4 focus-within:ring-blue-50">
          <div className="relative">
            <label htmlFor="video-caption" className="sr-only">Video caption</label>

            <textarea
              id="video-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={4}
              maxLength={maxCaptionLength}
              placeholder="Add a caption..."
              className="min-h-[105px] w-full resize-none border-none bg-white px-4 py-3.5 pr-12 text-sm leading-6 text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0"
            />

            {/* Emoji Button */}

            <button
              ref={smileyRef}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowEmoji((prev) => !prev);
              }}
              className={`absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200 ${showEmoji ? "bg-blue-50 text-blue-600" : "bg-gray-50 text-gray-400 hover:bg-blue-50 hover:text-blue-600"}`}
            >
              <PiSmileyFill size={20} />
            </button>

            <EmojiReactPicker set={setCaption} showEmoji={showEmoji} setShowEmoji={setShowEmoji} triggerRef={smileyRef} />
          </div>

          {/* Caption Footer */}

          <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/50 px-4 py-2">
            <span className="text-[11px] text-gray-400">Caption is optional</span>
            <span className={`text-[11px] font-medium ${caption.length > maxCaptionLength * 0.9 ? "text-orange-500" : "text-gray-400"}`}>
              {caption.length}/{maxCaptionLength}
            </span>
          </div>
        </div>

        {/* ================= POST ACTION ================= */}

        <div className="mt-4 flex items-center justify-between">
          <p className="hidden text-xs text-gray-400 sm:block">
            {files.length > 0 ? `${files.length} ${files.length === 1 ? "video" : "videos"} ready to publish` : "Add videos to continue"}
          </p>

          <div className={`ml-auto transition-all duration-200 ${files.length > 0 ? "opacity-100" : "pointer-events-none opacity-50"}`}>
            <PostButton type="submit" disabled={files.length === 0 || isSubmitting} />
          </div>
        </div>
      </form>
    </div>
  );
};

export default Video;
